import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  CssBaseline,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import InboxIcon from '@mui/icons-material/MoveToInbox';
import MailIcon from '@mui/icons-material/Mail';
import HomeIcon from '@mui/icons-material/Home';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LoginIcon from '@mui/icons-material/Login';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { getAuth, signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";

// Mock user data for the example
const mockUserData = {
  name: 'Shivamba ',
  email: '',
};

// -----------------------------------------------------------------------------
// Header Component
// -----------------------------------------------------------------------------
const Header = ({ onMenuClick }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const navigate = useNavigate();

  const handleLogout = async () => {
    const auth = getAuth();
    try {
      await signOut(auth);
      console.log("User signed out.");
      navigate("/login"); // 👈 redirect to home after logout
    } catch (error) {
      console.error("Logout failed:", error.message);
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const userInitial = mockUserData?.name?.charAt(0)?.toUpperCase() || 'U';

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        background: 'linear-gradient(90deg, #eb630fff 0%, #9c27b0 50%, #eb630fff 100%)',
      }}
    >
      <Toolbar>
        {/* <IconButton
          edge="start"
          color="inherit"
          onClick={onMenuClick}
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton> */}

        <Typography variant="b" noWrap sx={{ flexGrow: 1 }}>
          शिवअंबा पूजा साहित्य व प्रासादिक भांडार
        </Typography>

        <Box>
          <Tooltip title="Profile">
            <IconButton onClick={handleMenuOpen} size="small" sx={{ ml: 2 }}>
              <Avatar sx={{ width: 32, height: 32 }}>
                {userInitial}
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
            PaperProps={{
              elevation: 3,
              sx: { mt: 1.5 },
            }}
          >
            <MenuItem disabled>
              <Typography variant="subtitle1">
                {mockUserData?.name || mockUserData?.email || 'Guest'}
              </Typography>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

// -----------------------------------------------------------------------------
// Sidebar Component
// -----------------------------------------------------------------------------
const drawerWidth = 240;

const Sidebar = ({ isOpen, onClose }) => {
  const [openStudents, setOpenStudents] = useState(false);
  const [openSchools, setOpenSchools] = useState(false);

  return (
    <Drawer
      variant="temporary"
      open={isOpen}
      onClose={onClose}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          background: 'linear-gradient(to bottom, #eb630fff, #9c27b0)',
          color: '#fff',
          borderRight: 'none',
        },
      }}
      elevation={3}
    >
      {/* This toolbar adds padding at the top to clear the AppBar */}
      <Toolbar />
      <Box sx={{ overflow: 'auto', textAlign: 'center', p: 2 }}>
        <Typography variant="h5" sx={{ my: 2, fontWeight: 'bold' }}>
          ViperX
        </Typography>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />

      <List component="nav">
        {/* Dashboard */}
        <ListItemButton onClick={() => console.log('Navigating to Dashboard')} sx={styles.menuItem}>
          <ListItemIcon sx={styles.menuIcon}><HomeIcon /></ListItemIcon>
          <ListItemText primary="Dashboard" />
        </ListItemButton>

        {/* School/College */}
        <ListItemButton onClick={() => setOpenSchools(!openSchools)} sx={styles.menuItem}>
          <ListItemIcon sx={styles.menuIcon}><SchoolIcon /></ListItemIcon>
          <ListItemText primary="School/College" />
          {openSchools ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={openSchools} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton onClick={() => console.log('Navigating to Add School/College')} sx={styles.subMenuItem}>
              <ListItemText primary="Add School/College" />
            </ListItemButton>
          </List>
        </Collapse>

        {/* Users */}
        <ListItemButton onClick={() => setOpenStudents(!openStudents)} sx={styles.menuItem}>
          <ListItemIcon sx={styles.menuIcon}><PersonIcon /></ListItemIcon>
          <ListItemText primary="Users" />
          {openStudents ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={openStudents} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton onClick={() => console.log('Navigating to View Users')} sx={styles.subMenuItem}>
              <ListItemText primary="View Users" />
            </ListItemButton>
          </List>
        </Collapse>

        {/* Login History */}
        <ListItemButton onClick={() => console.log('Navigating to Login History')} sx={styles.menuItem}>
          <ListItemIcon sx={styles.menuIcon}><LoginIcon /></ListItemIcon>
          <ListItemText primary="Login History" />
        </ListItemButton>
      </List>
    </Drawer>
  );
};

const styles = {
  menuItem: {
    px: 2,
    py: 1.5,
    color: '#fff',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  menuIcon: {
    color: '#fff',
    minWidth: '40px',
  },
  subMenuItem: {
    pl: 5,
    py: 1,
    color: '#ddd',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
};

// -----------------------------------------------------------------------------
// Main App Component
// -----------------------------------------------------------------------------
const Index = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const theme = createTheme();

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <Header onMenuClick={toggleSidebar} />
        <Sidebar isOpen={isSidebarOpen} onClose={toggleSidebar} />
      </Box>
    </ThemeProvider>
  );
};

export default Index;
